import { forwardRef, useImperativeHandle } from "react";
import styled from "styled-components";
import CardGridSkeleton from "../gridCards/animations/CardGridSkeleton";
import { v4 as uuidv4 } from "uuid";
import React, { useContext, useEffect, useRef, useState } from "react";
import PaymentMethodGridCard from "../gridCards/PaymentMethodGridCard";
import * as Library from "../../../utils/Library";

//Assets
import ae from "../../../assets/images/billing/billingAE.png";
import masterCard from "../../../assets/images/billing/billingMastercard.png";
import visa from "../../../assets/images/billing/billingVisa.png";
import PopupContext from "../../../PopupContext";
import StripePopup from "../../pages/billing/billingInvoiceComponents/StripePopup";
import { AppQueryClient } from "../../../api/queryClient";

/* #region  SCOPED CSS */
const StyledPage = styled.div`
    & {
        overflow-y: scroll;
        height: 100%;
        margin-right: -24px;
        margin-left: -24px;
        padding-left: 24px;
        padding-right: 17px;
    }
`;

const StyledAddPaymentMethod = styled.div`
    & {
        display: flex;
        width: 264.96px;
        height: 419.45px;
        padding: 8px;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        gap: 16px;
        flex-shrink: 0;
        align-self: stretch;
        border-radius: 4px;
        border: 1px solid var(--backgrounds-lines-n-200, #262e3e);
        background: var(--backgrounds-lines-n-500, #141922);
        cursor: pointer;
    }
    &:hover {
        border: 1px solid var(--text-icons-blue-500, #00aeef);
        background: var(--backgrounds-lines-n-600, #1a202e);
        transition: all 0.2s ease-in-out;
    }
    .circle {
        display: flex;
        width: 60px;
        height: 60px;
        padding: 10px;
        justify-content: center;
        align-items: center;
        gap: 10px;
        border-radius: 119px;
        border: 1px solid var(--text-icons-blue-500, #00aeef);
    }
`;
/* #endregion */

export default forwardRef(function InfinityGridPaymentMethods(props, ref) {
    const pageLimit = props?.limit || 12;

    const gridLayoutStyle = {
        display: "grid",
        gridTemplateColumns: `repeat(auto-fill, minmax(${props?.minColumnWidth || "395px"}, 1fr))`,
        gap: "24px"
    };

    /* #region STATES */
    const scrollContainerRef = useRef(null);
    const [scroll, setScroll] = useState(true);
    const [minimumCardsFill, setminimumCardsFill] = useState(1); //props?.minimumCardsFill || pageLimit
    const requestEnabled = props?.requestEnabled ?? true;

    const [queryKey, setQueryKey] = useState(props?.queryKey ?? uuidv4());
    const { setShowStripePopup } = useContext(PopupContext);

    useEffect(() => {
        if (props?.queryKey) setQueryKey(props?.queryKey);
    }, [props?.queryKey]);

    const filters = props?.filters;

    const {
        data,
        isFetching: isLoading,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        refetch
    } = Library.usePostInfiniteQuery(
        queryKey,
        "getPaymentMethods",
        {
            companyID: localStorage.getItem("currentCompanyID"),
            limit: pageLimit,
            searchText: filters?.filters?.searchText || "",
            ...filters
        },
        () => {
            setScroll(true);
        },
        true,
        "Global",
        false,
        requestEnabled
    );

    const pageList = data?.pages;
    const nextDataCount = data?.pages[data?.pages.length - 1].data?.nextDataCount;
    /* #endregion */

    /* #region EFFECTS */
    useEffect(() => {
        handleScroll();

        setminimumCardsFill(getTotalCards() + 1);
    }, [data?.pages]);

    useEffect(() => {
        const scrollContainer = scrollContainerRef.current;
        if (scrollContainer && !isLoading && scroll && !isFetchingNextPage) {
            //console.log("scroll event added");
            scrollContainer.addEventListener("scroll", handleScroll);
        }

        return () => {
            //console.log("scroll event removed");
            scrollContainer?.removeEventListener("scroll", handleScroll);
        };
    }, [scrollContainerRef, isLoading, pageList, scroll, isFetchingNextPage, data?.pages]);
    /* #endregion */

    /* #region METHODS */
    const handleScroll = () => {
        const scrollContainer = scrollContainerRef.current;

        const scrolledRatio = scrollContainer.scrollTop / (scrollContainer.scrollHeight - scrollContainer.clientHeight);
        const isAtBottom = scrolledRatio >= 0.6;

        if (isAtBottom && !isLoading && hasNextPage && !isFetchingNextPage) {
            setScroll(false);
            fetchNextPage();
        }
    };

    const getTotalCards = () => {
        return pageList?.reduce((acc, it) => {
            return acc + it?.data?.result?.length;
        }, 0);
    };

    const handleReset = infiniteGridQuerykey => {
        scrollContainerRef.current.scrollTop = 0;
        setScroll(true);
        AppQueryClient.clear();

        const newQueryKeyTMP = [...infiniteGridQuerykey];
        newQueryKeyTMP["refresh"] = uuidv4();
        setQueryKey(newQueryKeyTMP);
    };
    /* #endregion */

    useImperativeHandle(ref, () => ({
        reset: handleReset
    }));

    const dataResults = data?.pages?.[0]?.data?.results;

    return (
        <StyledPage className={`library-scrollable ${props?.className || ""}`} ref={scrollContainerRef}>
            <StripePopup onSuccess={refetch} haveMoreThanOneData={dataResults?.length > 0} />
            {isLoading && !isFetchingNextPage ? (
                <div style={gridLayoutStyle}>
                    {Array.from({ length: 12 }, () => (
                        <CardGridSkeleton
                            key={uuidv4()}
                            height="417.28px"
                            minColumnWidth={props?.minColumnWidth || "395px"}
                        />
                    ))}
                </div>
            ) : (
                <div className="mb-24px" style={gridLayoutStyle}>
                    <AddNewPaymentMethod onClick={() => setShowStripePopup(true)} />

                    {dataResults?.map(item => {
                        // const isOwnAccount = item?._id === localStorage.getItem("BOstagesAccountID");
                        // const accesse = roleAccesse(props?.userRole, item?.role);
                        const image =
                            item?.stripeCardIssuer === "visa"
                                ? visa
                                : item?.stripeCardIssuer === "mastercard"
                                  ? masterCard
                                  : item?.stripeCardIssuer === "American Express"
                                    ? ae
                                    : item?.stripeCardIssuer === "unionpay"
                                      ? undefined //TODO: Missing Image
                                      : "";

                        return (
                            <PaymentMethodGridCard
                                key={item?.ourID}
                                className="w-100"
                                image={image}
                                title={item?.stripeCardIssuer}
                                id={item?.ourID}
                                isCardEnabled={item?.status?.toLowerCase() !== "suspended"}
                                isCardSuspended={item?.status?.toLowerCase() === "suspended"}
                                //---
                                highlighted={item?.isDefault}
                                highlightCustomText="PRIMARY"
                                highlightColor="#0060CC"
                                //---
                                data={{
                                    role: item?.role,
                                    labelBrandName: item?.labelBrandName,
                                    stripeCardNumber: "****" + item?.stripeCardNumber,
                                    stripeCardExpDate: item?.stripeCardExpDate,
                                    cvc: "***",
                                    country: "Norway"
                                }}
                                // defaultSecondTitle={props?.defaultSecondTitle}
                                edit={{
                                    hidden: true
                                }}
                                suspend={{
                                    hidden: true
                                }}
                                delete={
                                    props?.onDeleteAction && dataResults.length > 1
                                        ? {
                                              onClick: () => props?.onDeleteAction(item?._id, item?.stripeCardNumber)
                                          }
                                        : null
                                }
                                primaryButton={{
                                    title: dataResults.length > 1 ? "MAKE PRIMARY" : "REPLACE",
                                    onClick:
                                        dataResults.length > 1
                                            ? item?.isDefault
                                                ? undefined
                                                : () => props?.onPrimaryAction(item?._id, item?.stripeCardNumber)
                                            : () => console.log("Replace payment method")
                                }}
                                secondaryButton={
                                    dataResults.length > 1
                                        ? null
                                        : {
                                              title: "CANCEL FEE"
                                          }
                                }
                            />
                        );
                    })}

                    {!isFetchingNextPage &&
                        minimumCardsFill - getTotalCards() > 0 &&
                        Array.from({ length: minimumCardsFill - getTotalCards() }, (_, index) => (
                            <PaymentMethodGridCard
                                key={index}
                                isCardEnabled={false}
                                defaultSecondTitle={props?.defaultSecondTitle}
                            />
                        ))}

                    {isFetchingNextPage &&
                        Array.from({ length: nextDataCount }, (_, index) => (
                            <CardGridSkeleton
                                key={index}
                                height="417.28px"
                                minColumnWidth={props?.minColumnWidth || "395px"}
                            />
                        ))}
                </div>
            )}
        </StyledPage>
    );
});

const AddNewPaymentMethod = ({ onClick }) => {
    return (
        <StyledAddPaymentMethod onClick={onClick}>
            <div className="circle">
                <svg xmlns="http://www.w3.org/2000/svg" width="25" height="25" viewBox="0 0 25 25" fill="none">
                    <path
                        d="M13.665 5.5C13.665 4.94772 13.2173 4.5 12.665 4.5C12.1127 4.5 11.665 4.94772 11.665 5.5V11.5H5.66498C5.11269 11.5 4.66498 11.9477 4.66498 12.5C4.66498 13.0523 5.11269 13.5 5.66498 13.5H11.665V19.5C11.665 20.0523 12.1127 20.5 12.665 20.5C13.2173 20.5 13.665 20.0523 13.665 19.5V13.5H19.665C20.2173 13.5 20.665 13.0523 20.665 12.5C20.665 11.9477 20.2173 11.5 19.665 11.5H13.665V5.5Z"
                        fill="#00AEEF"
                    />
                </svg>
            </div>
            <span style={{ fontSize: "14px", color: "#D1D1D1" }}>ADD NEW PAYMENT METHOD</span>
        </StyledAddPaymentMethod>
    );
};
